import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ArrowUpRight, Newspaper, AlertTriangle, Pin } from 'lucide-react'
import Footer from '@/components/Footer'
import { useLanguage } from '@/localization'
import { useNews, formatDate, itemDate, type NewsItem, type NewsTag } from '@/useNews'
import type { CommunityData } from '@/useCommunity'
import { newsTagLabel } from '../../../shared/localization/core'

/** Visual style per tag chip (light theme). */
const tagStyles: Record<string, string> = {
  general: 'bg-uliunai-red/10 text-uliunai-red ring-uliunai-red/25',
  kf1: 'bg-uliunai-red/10 text-uliunai-red ring-uliunai-red/25',
  kf2: 'bg-stone-200 text-stone-600 ring-stone-300',
  minecraft: 'bg-green-600/10 text-green-700 ring-green-600/25',
}

function tagClass(tag?: NewsTag): string {
  if (!tag) return tagStyles.general
  return tagStyles[tag.toLowerCase()] ?? tagStyles.general
}

/**
 * /news — the full list of community (general-tagged) news, pinned-first then
 * newest. Each card links to its detail page. Mirrors the ConductPage / LegalDoc
 * standalone-page chrome (parchment, orbitron heading, shared Footer).
 */
export default function NewsListPage({ social }: { social: CommunityData['social'] }) {
  const state = useNews()
  const { t } = useLanguage()

  // Land at the top when navigating in from a teaser link.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="flex min-h-screen flex-col">
      <main className="mx-auto w-full max-w-5xl flex-1 px-6 py-16 sm:py-20">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 font-rajdhani text-sm font-semibold text-stone-500 transition-colors hover:text-uliunai-red"
        >
          <ArrowLeft size={16} />
          {t('Back to the hub')}
        </Link>

        <header className="mt-8 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-stone-line bg-white px-4 py-1.5 font-rajdhani text-xs font-semibold uppercase tracking-[0.25em] text-uliunai-red shadow-card">
            <Newspaper size={16} />
            {t('Latest updates')}
          </span>
          <h1 className="metal-text mt-5 font-orbitron text-4xl font-black tracking-tight sm:text-5xl">
            {t('Community News')}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl font-rajdhani text-base leading-relaxed text-stone-600">
            {t('Announcements, updates, and everything happening across the Uliunai community.')}
          </p>
        </header>

        <div className="mt-12">
          {state.status === 'loading' && <ListSkeleton />}
          {state.status === 'error' && <NewsError />}
          {state.status === 'ready' &&
            (state.items.length === 0 ? (
              <NewsEmpty />
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {state.items.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>
            ))}
        </div>
      </main>

      <Footer social={social} />
    </div>
  )
}

function NewsCard({ item }: { item: NewsItem }) {
  const { t } = useLanguage()
  const date = itemDate(item)

  return (
    <Link
      to={`/news/${item.id}`}
      className="group flex flex-col rounded-2xl border border-stone-line bg-white p-6 shadow-card transition-all duration-200 hover:-translate-y-1 hover:border-uliunai-red/30 hover:shadow-card-hover"
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {item.pinned && (
          <span className="inline-flex items-center gap-1 rounded-full bg-uliunai-red px-2.5 py-1 font-rajdhani text-xs font-bold uppercase tracking-wider text-white">
            <Pin size={11} />
            {t('Pinned')}
          </span>
        )}
        {item.tag && (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 font-rajdhani text-xs font-semibold uppercase tracking-wider ring-1 ${tagClass(item.tag)}`}
          >
            {t(newsTagLabel(String(item.tag)))}
          </span>
        )}
        {date && (
          <time
            dateTime={date}
            className="ml-auto font-rajdhani text-xs font-medium text-stone-500"
          >
            {formatDate(date)}
          </time>
        )}
      </div>

      <h2 className="font-orbitron text-lg font-bold leading-snug text-stone-900">{item.title}</h2>

      {item.excerpt && (
        <p className="mt-3 flex-1 font-rajdhani text-sm leading-relaxed text-stone-600">
          {item.excerpt}
        </p>
      )}

      <span className="mt-5 inline-flex items-center gap-1 font-rajdhani text-sm font-semibold text-uliunai-red transition-colors group-hover:text-uliunai-red-dark">
        {t('Read more')}
        <ArrowUpRight
          size={15}
          className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </span>
    </Link>
  )
}

function ListSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse rounded-2xl border border-stone-line bg-white p-6 shadow-card"
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="h-5 w-16 rounded-full bg-stone-200" />
            <div className="h-3 w-20 rounded bg-stone-200" />
          </div>
          <div className="mb-3 h-5 w-3/4 rounded bg-stone-200" />
          <div className="space-y-2">
            <div className="h-3 w-full rounded bg-stone-200" />
            <div className="h-3 w-5/6 rounded bg-stone-200" />
          </div>
        </div>
      ))}
    </div>
  )
}

function NewsEmpty() {
  const { t } = useLanguage()

  return (
    <div className="rounded-2xl border border-dashed border-stone-300 bg-white/60 px-6 py-16 text-center">
      <Newspaper size={28} className="mx-auto mb-3 text-stone-400" />
      <p className="font-rajdhani text-lg font-medium text-stone-700">{t('No news yet')}</p>
      <p className="mt-1 font-rajdhani text-sm text-stone-500">
        {t('Check back soon for community updates and announcements.')}
      </p>
    </div>
  )
}

function NewsError() {
  const { t } = useLanguage()

  return (
    <div className="rounded-2xl border border-uliunai-red/30 bg-uliunai-red/5 px-6 py-16 text-center">
      <AlertTriangle size={28} className="mx-auto mb-3 text-uliunai-red" />
      <p className="font-rajdhani text-lg font-medium text-stone-800">
        {t("Couldn't load the news")}
      </p>
      <p className="mt-1 font-rajdhani text-sm text-stone-500">
        {t('Please try again in a little while.')}
      </p>
    </div>
  )
}
